"use client";

import { useContext, useEffect } from "react";
import dynamic from "next/dynamic";
import { HeroModule } from "@/components/HeroModule";
import { ThreeStepsModule } from "@/components/ThreeStepsModule";
import { useWindowSize } from "@/hooks/browser/useWindowsSize";
import { useIntersectionObserver } from "@/hooks/observers/useIntersectionObserver";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { BannerWrapperSize } from "@/components/Banner/types";
import { I_HomePage } from "@/data/types/pages";
import { GridModuleSkeleton } from "@/components/GridModule";
import { QREditorSkeletonWrapper } from "@/components/QREditorSkeleton";

const QREditorHomeModule = dynamic(
  () =>
    import("@/components/QREditorHomeModule").then((mod) => ({
      default: mod.QREditorHomeModule,
    })),
  {
    ssr: false,
    loading: () => <QREditorSkeletonWrapper />,
  }
);

const GridModule = dynamic(
  () =>
    import("@/components/GridModule/GridModule").then((mod) => ({
      default: mod.GridModule,
    })),
  { ssr: false, loading: () => <GridModuleSkeleton /> }
);

const AIModule = dynamic(() => import("@/components/AIModule").then(({ AIModule }) => AIModule), {
  ssr: false,
});

const KeepItSimpleModule = dynamic(
  () =>
    import("@/components/KeepItSimpleModule").then(({ KeepItSimpleModule }) => KeepItSimpleModule),
  {
    ssr: false,
  }
);

const TabsModule = dynamic(
  () => import("@/components/TabsModule").then(({ TabsModule }) => TabsModule),
  {
    ssr: false,
  }
);

const MoreModule = dynamic(
  () => import("@/components/MoreModule").then(({ MoreModule }) => MoreModule),
  {
    ssr: false,
  }
);

export const HomePageTemplate = ({
  data,
  amplitudeEvent,
}: {
  data: I_HomePage;
  amplitudeEvent?: string;
}) => {
  const screenSize = useWindowSize();
  const { setQrSelected } = useContext(AmplitudeContext);
  const { ref: gridModuleRef, isIntersecting: shouldLoadGridModule } =
    useIntersectionObserver<HTMLDivElement>({
      rootMargin: screenSize?.isTablet ? "-100px" : "200px",
      triggerOnce: true,
    });

  const getWrapperSize = (isTablet: BannerWrapperSize, isNotTablet: BannerWrapperSize) => {
    return screenSize?.isTablet ? isTablet : isNotTablet;
  };

  useEffect(() => {
    setQrSelected(undefined);
    amplitudeEvent && amplitudeTrackEvent(amplitudeEvent);
  }, []);

  return (
    <main>
      {data.qrEditorCompact && <QREditorHomeModule data={data.qrEditorCompact} />}

      {data.billboard && (
        <HeroModule
          data={data.billboard}
          wrapperSize={getWrapperSize("sm", "lg")}
        />
      )}

      <ThreeStepsModule
        data={data.featured2}
        wrapperSize={getWrapperSize("xs", "md")}
      />

      <AIModule data={data.featuredAI} />

      <div ref={gridModuleRef}>
        {shouldLoadGridModule && (
          <GridModule
            data={data.previewHome}
            wrapperSize={getWrapperSize("xs", "lg")}
            previewTranslations={data.previewTranslations}
            defaultPreviewTitle={data.previewHome.defaultPreviewTitle}
          />
        )}
      </div>

      <KeepItSimpleModule
        data={data.banner}
        wrapperSize={getWrapperSize("sm", "lg")}
      />

      <TabsModule
        data={{
          title: data.faqsHome.title,
          faqsHomeTabs: data.faqsHomeTabs,
        }}
        wrapperSize="md"
        backgroundColor={data.faqsHome.backgroundColor}
        customClass={data.faqsHome.customClass}
      />

      <MoreModule
        data={data.banner2}
        wrapperSize="md"
      />
    </main>
  );
};
